/**
 * CalendarDayCell Component
 * Single day cell in the week calendar
 */

import React from 'react';
import { cn } from '@/utils/cn';
import TripChip from './TripChip';

interface TripSummary {
  id: number;
  qNumber: string;
  status: string;
  fromAirport?: string;
  toAirport?: string;
}

interface CalendarDayCellProps {
  date: Date;
  isToday: boolean;
  trips: TripSummary[];
  onTripClick?: (tripId: number) => void;
  onDayClick?: (date: Date) => void;
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  date,
  isToday,
  trips,
  onTripClick,
  onDayClick,
}) => {
  const dayName = dayNames[date.getDay()];
  const dayNumber = date.getDate();

  return (
    <div
      className={cn(
        'flex flex-col min-w-[80px] sm:min-w-[100px] border-r border-border last:border-r-0',
        'cursor-pointer hover:bg-muted/50 transition-colors'
      )}
      onClick={() => onDayClick?.(date)}
    >
      {/* Day Header */}
      <div
        className={cn(
          'text-center py-2 border-b border-border',
          isToday && 'bg-primary/10'
        )}
      >
        <p className={cn(
          'text-xs font-medium uppercase tracking-wide',
          isToday ? 'text-primary' : 'text-muted-foreground'
        )}>{dayName}</p>
        <p
          className={cn(
            'text-lg font-semibold',
            isToday ? 'text-primary' : 'text-foreground'
          )}
        >
          {dayNumber}
        </p>
      </div>

      {/* Trips Container */}
      <div className="flex-1 p-1 space-y-1 min-h-[60px]">
        {trips.slice(0, 3).map((trip) => (
          <TripChip
            key={trip.id}
            tripId={trip.id}
            qNumber={trip.qNumber}
            fromAirport={trip.fromAirport}
            toAirport={trip.toAirport}
            status={trip.status}
            onClick={() => onTripClick?.(trip.id)}
          />
        ))}
        {trips.length > 3 && (
          <p className="text-xs text-center text-muted-foreground font-medium">
            +{trips.length - 3} more
          </p>
        )}
      </div>
    </div>
  );
};

export default CalendarDayCell;
